<template>
  <div v-if="vulnerable" class="report-icon-field">
    <label for="report-icon-svg">
      Icono SVG para el reporte
      <FieldHelp label="el icono SVG del reporte" :text="helpText" />
    </label>
    <div>
      <input
        id="report-icon-svg"
        ref="fileInput"
        type="file"
        accept=".svg,image/svg+xml"
        @change="onFileChange"
      />
      <small class="report-icon-hint">
        Opcional. SVG vectorial, máximo 24 KB. Se ajustará automáticamente al reporte.
      </small>
      <div class="report-icon-specifications">
        <small><strong>Para generar con IA:</strong> {{ aiSpecifications }}</small>
        <button type="button" @click="copySpecifications">{{ specificationsCopied ? 'Copiado' : 'Copiar especificaciones' }}</button>
      </div>
      <small v-if="errorMessage" class="error-text">{{ errorMessage }}</small>

      <div class="report-icon-preview">
        <span class="report-icon-preview__image" aria-hidden="true">
          <span v-if="safePreview" v-html="safePreview"></span>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round">
            <path d="M12 3L21 8L12 13L3 8Z" />
            <path d="M3 12L12 17L21 12" />
            <path d="M3 16L12 21L21 16" />
          </svg>
        </span>
        <span>{{ safePreview ? 'SVG personalizado' : 'Se utilizará el SVG predeterminado' }}</span>
        <button v-if="value" type="button" class="report-icon-remove" @click="removeIcon">
          Usar predeterminado
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import FieldHelp from '@/components/FieldHelp.vue';
import {
  readReportIconSvgFile,
  validateAndNormalizeReportIconSvg,
} from '@/services/layers/reportIconSvg.js';

export default {
  name: 'VulnerableReportIconField',
  components: { FieldHelp },
  props: {
    value: { type: String, default: '' },
    vulnerable: { type: Boolean, default: false },
  },
  data() {
    return {
      errorMessage: '',
      safePreview: '',
      specificationsCopied: false,
      aiSpecifications: 'archivo .svg descargable, máximo 24 KB; icono vectorial simple, centrado, sin fondo y legible a tamaño pequeño; con viewBox o dimensiones numéricas; formas geométricas y colores planos; sin texto, fotografías, imágenes incrustadas, enlaces, scripts, degradados, máscaras ni clipPath.',
      helpText: 'Icono opcional para representar esta capa vulnerable en el reporte. Admite distintos viewBox o dimensiones numéricas y normaliza metadatos comunes. Si se omite se usa el icono predeterminado.',
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        try {
          this.safePreview = validateAndNormalizeReportIconSvg(value);
        } catch (error) {
          this.safePreview = '';
        }
      },
    },
  },
  methods: {
    async copySpecifications() {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.aiSpecifications);
        } else {
          const textarea = document.createElement('textarea');
          textarea.value = this.aiSpecifications;
          textarea.setAttribute('readonly', '');
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }
        this.specificationsCopied = true;
        window.setTimeout(() => { this.specificationsCopied = false; }, 1800);
      } catch (error) {
        this.errorMessage = 'No fue posible copiar las especificaciones automáticamente.';
      }
    },
    async onFileChange(event) {
      this.errorMessage = '';
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      try {
        const normalized = await readReportIconSvgFile(file);
        this.safePreview = normalized;
        this.$emit('input', normalized);
      } catch (error) {
        this.errorMessage = error.message;
        event.target.value = '';
      }
    },
    removeIcon() {
      this.errorMessage = '';
      this.safePreview = '';
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      this.$emit('input', '');
    },
  },
};
</script>

<style scoped>
.report-icon-field { display: contents; }
.report-icon-hint { display: block; margin-top: 5px; color: #666; }
.report-icon-specifications { display: flex; align-items: flex-start; gap: 8px; margin-top: 6px; max-width: 760px; }
.report-icon-specifications small { color: #6b7280; font-size: 10px; line-height: 1.35; }
.report-icon-specifications button { flex: none; border: 0; background: transparent; color: #0056b3; cursor: pointer; font-size: 10px; padding: 0; text-decoration: underline; }
.report-icon-preview { display: flex; align-items: center; gap: 10px; margin-top: 10px; color: #555; }
.report-icon-preview__image { display: inline-flex; width: 38px; height: 38px; padding: 7px; color: #0b2b4c; background: #eef2f6; border-radius: 7px; }
.report-icon-preview__image span,
.report-icon-preview__image svg { width: 100%; height: 100%; }
.report-icon-preview__image span { display: inline-flex; }
.report-icon-preview__image span ::v-deep svg { width: 100%; height: 100%; }
.report-icon-remove { border: 0; background: transparent; color: #0056b3; cursor: pointer; text-decoration: underline; }
</style>
